import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertTitle,
  Badge,
  Box,
  Button as ChakraButton,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Skeleton,
  SkeletonText,
  Spinner,
  Stack,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiDownload, FiInfo } from 'react-icons/fi';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  BiLinkExternal,
  FaEye,
  FaEyeSlash,
  FiExternalLink,
} from 'react-icons/all';
import { useForm } from 'react-hook-form';
import { Button, Heading, StatusBadge, Table, Textarea } from '~/components';
import { ALL_FEATURES, ROUTER_PATHS } from '~/constants';
import {
  useAuth,
  useCan,
  useExternoCan,
  useIsComissioned,
  useIsInternal,
  useRoleCan,
  useViewCommission,
} from '~/hooks';
import { useContract } from '~/hooks/useContracts';
import { useEquipmentById } from '~/hooks/useEquipments';
import {
  getBillPdf,
  getContractBusinessInformation,
  getContractEquipamentsXlsx,
  getExtensionContractDocument,
  getInvoicePdf,
  updateExtensionZeroCarbon,
} from '~/services';
import {
  ContractView,
  Extension,
  StatusApoliceEquipamentoEnum,
  StatusExtensaoIdEnum,
} from '~/types';
import { replaceParameters } from '~/utils/replaceParamaters';
import { useDocumentsByContractId } from '~/hooks/useDocuments/useDocuments';
import { downloadDocument } from '~/services/Documents';
import { ExtensionRequestForm } from './ExtensionRequestForm';
import { ContractBusinessInformation } from '~/types/businesseInformation';
import { CancelContractExtension } from '~/pages/Portal/Contract/CancelContractExtension';
import { Shortcut } from '~/components/Shortcut';
import shortcut from '~/assets/shortcut_technical_support.svg';
import { EquipmentSuggestionDialog } from '~/pages/Portal/Contract/EquipmentSuggestionDialog';
import { InsuranceDialog } from '~/pages/Portal/Contract/InsuranceDialog';
import { useInsuranceList } from '~/hooks/useInsurance/useInsurance';
import { getInsuranceDocument } from '~/services/Insurance';
import { isDateInPast } from '~/utils/formatDate';
import { ZeroCarbonLogo } from '~/components/ZeroCarbonLogo';
import { formatCurrency } from '~/utils/formatCurrency';
import {
  downloadCertificate,
  getTodosCertificadosContrato,
} from '~/services/GroupZeroCarbon';
import { openLinkInNewTab } from '~/utils/openLinkInNewTab';
import { CertificateContractsView } from '~/types/zeroCarbon';
import { useFeatureFlags } from '~/contexts/FeatureFlagsContext/FeatureFlagsContext';
import { ContractCharts } from '~/pages/Portal/StrategicMap/ContractCharts';
import { AnalyseContractExtensionBox } from '~/pages/Portal/Contract/AnalyseContractExtensionBox';
import api from '~/utils/api';

export const ContractDetail: React.VFC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    register,
    handleSubmit,
    formState,
    setValue,
    reset,
    clearErrors,
    setError,
    control,
  } = useForm();
  const toast = useToast();
  const features = useFeatureFlags();
  const [selectedEquipment, setSelectedEquipment] = useState<string>('');
  const history = useHistory();
  const { data, isLoading, refetch: refetchContract } = useContract(Number(id));
  useEffect(() => {
    setValue('anotacao1', data?.anotacao1 || '');
    setValue('anotacao2', data?.anotacao2 || '');
    setAnotacao1Original(data?.anotacao1 || '');
    setAnotacao2Original(data?.anotacao2 || '');
  }, [data]);
  const [anotacao1Original, setAnotacao1Original] = useState<string>('');
  const [anotacao2Original, setAnotacao2Original] = useState<string>('');
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isDownloadingBill, setIsDownloadingBill] = useState(false);
  const [expandedEquipment, setExpandedEquipment] = useState<number>(-1);
  const [expandedInfo, setExpandedInfo] = useState<number>(-1);
  const [canShowInfo, setCanShowInfo] = useState<boolean>(false);
  const [businessInfo, setBusinessInfo] = useState<
    ContractBusinessInformation[] | undefined
  >(undefined);
  const [loadingBusinessInfo, setLoadingBusinessInfo] =
    useState<boolean>(false);
  const { user } = useAuth();
  const {
    data: equipmentData,
    isLoading: isEquipmentLoading,
    isError: isEquipmentError,
    refetch,
  } = useEquipmentById(selectedEquipment, {
    refetchOnWindowFocus: false,
    enabled: false,
  });
  const [selectedExtension, setSelectedExtension] = useState<Extension>(
    {} as Extension,
  );
  const internoCan = useRoleCan({ roles: ['administrador', 'operacional'] });
  const userIsOperacional = useRoleCan({ roles: ['operacional'] });
  const roleAdmin = useRoleCan({ roles: ['administrador'] });
  const roleCommercialManager = useRoleCan({ roles: ['gerente de contas'] });
  const externoCan = useExternoCan({ roles: ['cliente'] });

  const userIsAdmin = useCan({ accessTypes: ['ADMIN'] });
  const isComissioned = useIsComissioned();
  const viewCommission = useViewCommission();
  const commercialManager =
    useRoleCan({
      roles: ['gerente de contas'],
    }) &&
    (isComissioned || viewCommission);
  const commercialPartner = useRoleCan({ roles: ['parceiro comercial'] });
  const businessInformationAllow =
    userIsAdmin || commercialManager || commercialPartner;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isInterno = useIsInternal();

  const [isFetchingUpdateZeroCarbon, setIsFetchingUpdateZeroCarbon] =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useState<boolean>(false);

  const {
    isOpen: isEquipmentOpen,
    onOpen: onEquipmentOpen,
    onClose: onEquipmentClose,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useDisclosure();
  const {
    isOpen: isExtensionOpen,
    onOpen: onExtensionOpen,
    onClose: onExtensionClose,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useDisclosure();
  const { data: documentData, isLoading: isDocumentLoading } =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useDocumentsByContractId(Number(id));
  const {
    data: insuranceData,
    isLoading: isInsuranceLoading,
    refetch: refetchInsurance,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useInsuranceList(Number(id));

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useDisclosure();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [certificados, setCertificados] = useState<CertificateContractsView[]>(
    [],
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isLoagingCertificados, setIsLoadingCertificados] = useState(false);
  const [isDownloadingCertificados, setIsDownloadingCertificados] =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useState(false);

  const newExpirationDate = (date: Date, daysInFuture = 1) => {
    date.setDate(date.getDate() + daysInFuture);
    return date;
  };

  const parseDate = (date: string | undefined) => {
    if (date) {
      const d = date?.split('-');
      return new Date(
        parseInt(d[0], 10),
        parseInt(d[1], 10) - 1,
        parseInt(d[2], 10),
      );
    }
    return new Date();
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  function handleDocumentDownload(id_doc: number) {
    downloadDocument(id_doc)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  }

  function handleDownloadInvoice(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingInvoice(true);
    getInvoicePdf(cd_fatura)
      .then(response => {
        setIsDownloadingInvoice(false);
        download(
          atob(response?.data),
          `fatura_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingInvoice(false);
        if (e?.response?.status === 404) {
          console.log(e.response.data.message);
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar fatura',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  function handleDownloadBill(cd_fatura: string, nu_fatura: string) {
    setIsDownloadingBill(true);
    getBillPdf(cd_fatura)
      .then(response => {
        setIsDownloadingBill(false);
        download(
          atob(response?.data),
          `boleto_${cd_fatura}_${nu_fatura}`,
          'application/pdf',
        );
      })
      .catch((e: any) => {
        setIsDownloadingBill(false);
        if (e?.response?.status === 404) {
          console.log(e.response.data.message);
          toast({
            title: e.response.data.message,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Falha ao baixar boleto',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
  }

  function handleOpenExtension(extension: Extension) {
    setSelectedExtension(extension);
    onExtensionOpen();
  }

  function handleOpenEquipment(equipmentId: string) {
    setSelectedEquipment(equipmentId);
    onEquipmentOpen();
  }

  function getMessage(c: ContractView): string {
    switch (c.status_proposta) {
      case 0:
        return 'Avalie se o contrato é elegivel para extensão.';
      case 1:
        return externoCan && c?.possui_faturas_atraso
          ? 'Regularize os pagamentos pendentes para realizar extensão do Contrato.'
          : externoCan && !c?.possui_faturas_atraso
          ? 'Conheça as condições especiais que preparamos para você.'
          : !externoCan && !internoCan
          ? 'Informe o responsável por extensões de Contrato dentro da sua empresa.'
          : !externoCan && internoCan
          ? 'Proposta de renovação enviada ao cliente.'
          : '';
      case 2:
        return c?.pode_estender_contrato && c?.possui_faturas_atraso
          ? 'Regularize os pagamentos pendentes.'
          : 'O contrato não é elegível para extensão.';
      case 3:
        return 'Aguardando assinatura dos responsáveis.';
      default:
        return '';
    }
  }

  function mostraBoxExtensao(c: undefined | ContractView): boolean {
    if (!ALL_FEATURES) {
      return false;
    }
    if (c != null && c.status_proposta == null) {
      return false;
    }
    if (
      !internoCan && // Cliente
      c &&
      (c.status_proposta === 0 || // Aguardando análise
        c.status_proposta === 9 || // Elegível pelo robô
        c.status_proposta === 10) // Não elegível pelo robô
    ) {
      return false;
    }
    return true;
  }

  const mostrarMenuOpcoes = (c: undefined | ContractView): boolean => {
    if (
      !c ||
      !internoCan ||
      !c?.pode_cancelar_extensao ||
      c?.status_proposta === StatusExtensaoIdEnum.NAO_ELEGIVEL_EXTENSAO ||
      c?.status_proposta === StatusExtensaoIdEnum.FINALIZADA
    ) {
      return false;
    }
    return true;
  };

  const handleDownload = async (contract_id: string) => {
    getExtensionContractDocument(
      contract_id,
      data?.status_proposta ? data.status_proposta : 0,
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: `Erro`,
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };
  const mostrarInformacoesComerciais = () => {
    if (loadingBusinessInfo) {
      return;
    }
    if (canShowInfo || !!businessInfo) {
      setCanShowInfo(!canShowInfo);
      return;
    }
    setLoadingBusinessInfo(true);
    getContractBusinessInformation(id)
      .then(r => {
        setCanShowInfo(!canShowInfo);
        setBusinessInfo(r);
      })
      .catch(err => {
        if (err.response.status === 403) {
          toast({
            title: 'Sem permissão',
            description:
              'Você não tem permissão para visualizar essas informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: 'Erro',
            description: 'Erro ao buscar informações comerciais',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setLoadingBusinessInfo(false);
      });
  };
  const handleDownloadXlsx = async () => {
    setIsLoadingDownload(true);
    getContractEquipamentsXlsx(id)
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const handleDownloadInsurance = async (id_insurance: number) => {
    getInsuranceDocument(id_insurance)
      .then(r => {
        if (r.data.size > 0) {
          const file = new Blob([r.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(file);
          window.open(fileURL);
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: `Erro`,
          description: err.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };
  const getUltimoValorContrato = (
    contrato: ContractView | undefined,
  ): number => {
    if (contrato && contrato.extensoes.length > 1) {
      return Number(
        contrato.extensoes.filter(item => item.ultimo_aditivo)[0]
          .vr_totalmensalinstrumento,
      );
    }
    return Number(contrato?.vr_mensal_atual) || 0;
  };

  const updateZeroCarbon = async (id_adendoaditivo: number, value: boolean) => {
    setIsFetchingUpdateZeroCarbon(true);
    await updateExtensionZeroCarbon(id, id_adendoaditivo.toString(), value)
      .then(() => refetchContract())
      .catch(err => {
        toast({
          title: 'Erro ao atualizar Zero Carbon',
          description: err.response?.data?.detail || '',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsFetchingUpdateZeroCarbon(false);
      });
  };

  const carregarCertificados = (id_contrato: string, id_adendo: string) => {
    setIsLoadingCertificados(true);
    getTodosCertificadosContrato(id_contrato, id_adendo)
      .then(res => {
        setCertificados(res);
      })
      .finally(() => {
        setIsLoadingCertificados(false);
      });
  };

  const downloadCertificados = (
    id_certificado: string,
    tipo: 'certificadora' | 'validadora',
  ) => {
    setIsDownloadingCertificados(true);
    downloadCertificate(id_certificado, tipo)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.message,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloadingCertificados(false);
      });
  };
  const salvarAnotacoes = (form: any) => {
    const { anotacao1, anotacao2 } = form;
    if (anotacao1 !== anotacao1Original || anotacao2 !== anotacao2Original) {
      const formData = new FormData();
      formData.append('anotacao1', anotacao1);
      formData.append('anotacao2', anotacao2);
      return api
        .post(`/contratos/${id}/salvar_anotacoes`, formData)
        .then(() => {
          toast({
            title: 'Anotação salva com sucesso!',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        })
        .finally(() => {
          refetchContract();
        });
    }
    return {};
  };

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <Box px="8" py="4" paddingBottom="8rem">
      <Heading hasBackButton>Contrato N° {data?.nu_contrato}</Heading>
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="6"
        style={{
          position: 'relative',
        }}
      >
        <Stack maxW={390} w="100%" gridGap={4}>
          <Heading fontSize="md">Informações Gerais</Heading>
          <Flex justify="space-between" align="baseline">
            <Text
              fontWeight="medium"
              color="gray.800"
              fontSize="14px"
              whiteSpace="nowrap"
            >
              Locado por:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              {data?.locador || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              N° termo/pedido de locação:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nu_pedido}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data início da cobrança:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedInitialDate}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data encerramento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.mes_encerramentocontrato}
            </Text>
          </Flex>
          {/* <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Vigência do contrato em meses:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nu_mesescontrato ?? '-'}
            </Text>
          </Flex>
          */}
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Contrato estendido?
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.extensoes.length
                ? data?.extensoes.length > 1
                  ? 'Sim'
                  : 'Não'
                : '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Status Pagamento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <StatusBadge variant={data?.colorScheme || ''}>
                {data?.status_pgto}
              </StatusBadge>
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Observações:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              {data?.ds_observacao || '-'}
            </Text>
          </Flex>
        </Stack>
        <Center
          position="relative"
          bottom="0"
          display={{ md: 'block', sm: 'none' }}
        >
          <Divider orientation="vertical" borderColor="gray.300" />
        </Center>
        <Stack maxW={390} w="100%" gridGap={4}>
          <Heading fontSize="md">Dados da Contratante</Heading>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Nome Fantasia:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <Link
                to={replaceParameters(
                  ROUTER_PATHS.PORTAL.ADMIN.COMPANY_DETAIL,
                  String(data?.empresas?.id_empresa) || '',
                )}
              >
                {data?.empresas?.nm_fantasia || '-'}
              </Link>
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Razão Social:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas?.nm_razaosocial || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              CNPJ:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas?.cnpjFormatado || '-'}
            </Text>
          </Flex>
          {userIsAdmin && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Código Cliente Positivo:
              </Text>
              <Text color="gray.500" fontSize="14px">
                {data?.empresas.cd_usuariosap || '-'}
              </Text>
            </Flex>
          )}
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Grupo Econômico:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas?.grupoeconomico?.nm_grupoeconomico || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              É responsável por pagar a fatura?
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresas?.tp_pagadorfatura === '1' ? 'Sim' : 'Não'}
            </Text>
          </Flex>
        </Stack>
        <Center
          position="relative"
          bottom="0"
          display={{ md: 'block', sm: 'none' }}
        >
          <Divider orientation="vertical" borderColor="gray.300" />
        </Center>
        <Stack maxW={390} w="100%" gridGap={4}>
          <Heading fontSize="md">Valores</Heading>
          {/* <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Valor total:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedTotalValue}
            </Text>
          </Flex> */}
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Valor mensal atual:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.formattedMensalAtualValue}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Índice de correção:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.nm_indicecorrecao || '-'}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Mês de ajuste do contrato:
            </Text>
            <Text color="gray.500" fontSize="14px" textTransform="capitalize">
              {data?.formattedMonth}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Situação do Faturamento:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <Badge
                colorScheme={data?.st_faturamento === 0 ? 'green' : 'red'}
                px={2}
                py={1}
                borderRadius={4}
                w="150px"
                textAlign="center"
                fontSize="10px"
              >
                {data?.stFaturamentoDesc}
              </Badge>
            </Text>
          </Flex>
          {data?.possui_projeto_zero_carbono && internoCan && (
            <HStack justify="space-between">
              <Stack>
                <Heading fontSize="md">Zero Carbon</Heading>
                {data?.possui_certificado_projeto_zero_carbono && (
                  <Text color="gray.500" fontSize="14px">
                    Este contrato possui certificado Zero Carbon.
                  </Text>
                )}
                {!data?.possui_certificado_projeto_zero_carbono && (
                  <Text color="gray.500" fontSize="14px">
                    Certificado pendente de emissão.
                  </Text>
                )}
              </Stack>
              <ZeroCarbonLogo
                width={92}
                height={90}
                pendente={!data?.possui_certificado_projeto_zero_carbono}
                emitido={data?.possui_certificado_projeto_zero_carbono}
              />
            </HStack>
          )}
          {data?.possui_projeto_zero_carbono &&
            data?.possui_certificado_projeto_zero_carbono &&
            !internoCan && (
              <HStack justify="space-between">
                <Stack>
                  <Heading fontSize="md">Zero Carbon</Heading>
                  <Text color="gray.500" fontSize="14px">
                    Este aditivo do contrato possui certificado Haas Zero
                    Carbon.
                  </Text>
                </Stack>
                <ZeroCarbonLogo width={92} height={90} emitido />
              </HStack>
            )}
        </Stack>
      </Flex>
      {!features.analiseAutomaticaExtensaoEnabled && mostraBoxExtensao(data) && (
        <Box maxW="1080px" mt="30px">
          <Flex flexDirection="row" justifyContent="space-between" p="10px">
            <Heading fontSize="md" mb={0}>
              Extensão do Contrato
            </Heading>
            <HStack gridGap={4}>
              {mostrarMenuOpcoes(data) && (
                <Menu>
                  <MenuButton
                    fontWeight="bold"
                    whiteSpace="nowrap"
                    borderWidth="1px"
                    borderStyle="solid"
                    borderColor="blackAlpha.500"
                    padding="0 16px"
                    height="40px"
                    borderRadius="0.375rem"
                  >
                    Opções
                    <Icon as={FiChevronDown} fontSize="xl" ml={10} />
                  </MenuButton>
                  <MenuList>
                    <CancelContractExtension
                      id_contrato={id}
                      isDisabled={!data?.pode_cancelar_extensao}
                    />
                  </MenuList>
                </Menu>
              )}
              <ExtensionRequestForm
                id_contrato={Number(id)}
                valor_mensal_contrato={getUltimoValorContrato(data)}
                needsExtensionFile={data?.precisa_termo_extensao || false}
                extensionProposal={data?.proposta_extensao}
                visualization={!!data?.proposta_extensao}
                defaultCollectDate={newExpirationDate(
                  parseDate(data?.vencimento_final),
                  30,
                )}
                showButtonToSeeAnalyse={
                  internoCan && data?.status_proposta !== 3
                }
                minCollectDate={newExpirationDate(
                  parseDate(data?.vencimento_final),
                )}
                expirationDate={parseDate(data?.vencimento_final)}
                termNumberMeetsStandard={!!data?.nu_termo_segue_padrao}
                termNumber={data?.nu_termo_sugerido}
              />
              {externoCan &&
                data?.status_proposta === 1 &&
                !data?.possui_faturas_atraso && (
                  <Button
                    color="white"
                    background="gray.900"
                    _hover={{
                      bgColor: 'black',
                    }}
                    onClick={() =>
                      history.push(`/portal/contracts-extension/${id}`)
                    }
                  >
                    Estender Contrato
                  </Button>
                )}
              {data?.status_proposta === 3 && (
                <Button
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  onClick={() => handleDownload(id)}
                >
                  Visualizar proposta de extensão
                </Button>
              )}
            </HStack>
          </Flex>
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            paddingLeft="10px"
            paddingRight="10px"
          >
            <Alert
              bg="#f7f2fa"
              borderRadius="md"
              color="#9246ce"
              fontSize="sm"
              py={1}
              px={2}
            >
              <AlertTitle>Atenção:</AlertTitle>

              {/* mudar condição para o status da extensão no contrato */}
              <AlertDescription>
                <Text color="black">
                  Este contrato irá encerrar em{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {data?.formattedVencimentoFinal}
                  </span>
                  . {data ? getMessage(data) : ''}
                </Text>
              </AlertDescription>
            </Alert>
          </Flex>
          {user && user.type === 'Interno' && (
            <Flex
              flexDirection="row"
              justifyContent="space-between"
              paddingLeft="10px"
              paddingRight="10px"
            >
              <EquipmentSuggestionDialog id_contrato={data?.id_contrato} />
            </Flex>
          )}
        </Box>
      )}
      {features.analiseAutomaticaExtensaoEnabled &&
        mostraBoxExtensao(data) &&
        data && (
          <AnalyseContractExtensionBox
            id_contrato={id}
            contrato={data}
            handleDownload={handleDownload}
            user={user}
          />
        )}
      <Flex
        flexFlow="column"
        mt="30px"
        flexDir="row"
        gridGap="12"
        justifyContent="space-between"
        alignItems="end"
      >
        <Tabs variant="enclosed">
          <TabList>
            <Tab>Histórico</Tab>
            <Tab>Equipamentos</Tab>
            <Tab>
              Histórico de cobranças&nbsp;
              <Tooltip
                hasArrow
                label="As faturas podem conter mais de um contrato"
                bg="gray"
                shouldWrapChildren
              >
                <Icon as={FiInfo} />
              </Tooltip>
            </Tab>
            <Tab>Documentos</Tab>
            {data?.contratos_relacionados &&
              data?.contratos_relacionados.length > 0 && (
                <Tab>Contratos relacionados</Tab>
              )}
            {businessInformationAllow && <Tab>Informações Comerciais</Tab>}
            {isInterno && !roleCommercialManager && <Tab>Seguro</Tab>}
            {features.abaFinanceiroDetalheContratoEnabled &&
              data?.tp_situacao === '1' &&
              isInterno && <Tab>Financeiro</Tab>}
            {features.abaAnotacoesDetalheContratoEnabled && (
              <Tab>Anotações</Tab>
            )}
          </TabList>

          <TabPanels>
            <TabPanel>
              {!!data?.extensoes.length && (
                <Flex flexFlow="column" mt="30px">
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th>Início</Th>
                        <Th>Tipo</Th>
                        <Th>N° instrumento</Th>
                        <Th>Quantidade de faturas</Th>
                        <Th>Último valor mensal</Th>
                        {userIsAdmin && (
                          <>
                            <Th>
                              Zero Carbon{' '}
                              {isFetchingUpdateZeroCarbon && (
                                <Spinner size="sm" ml={2} />
                              )}
                            </Th>
                          </>
                        )}
                        <Th>Certificado</Th>
                        <Th>Consumo total</Th>
                        <Th>Compensado</Th>
                        <Th>Saldo</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.extensoes.map(extensao => (
                        <Tr key={extensao.id_adendoaditivo}>
                          <Td>{extensao.formattedInitialDate}</Td>
                          <Td>{extensao.tipo}</Td>
                          <Td>{extensao.cd_adendoaditivo || '-'}</Td>
                          <Td>
                            {extensao.nu_mesescontrato ||
                              extensao.differenceInMonths}
                          </Td>
                          <Td>{extensao.formattedMonthlyValue}</Td>
                          {userIsAdmin && (
                            <Td>
                              <Stack position="relative">
                                <Switch
                                  id="status"
                                  size="md"
                                  isDisabled={
                                    !extensao.vigente ||
                                    isFetchingUpdateZeroCarbon
                                  }
                                  colorScheme="green"
                                  isChecked={extensao.possui_zero_carbono}
                                  onChange={e => {
                                    updateZeroCarbon(
                                      extensao.id_adendoaditivo,
                                      e.target.checked,
                                    );
                                  }}
                                />
                              </Stack>
                            </Td>
                          )}
                          <Td>
                            <HStack gridGap={2} whiteSpace="nowrap">
                              {!!extensao.zero_carbono && (
                                <ZeroCarbonLogo
                                  emitido={extensao.zero_carbono.situacao === 0}
                                  pendente={
                                    extensao.zero_carbono.situacao !== 0
                                  }
                                />
                              )}
                              {extensao.zero_carbono ? (
                                <Text
                                  onClick={() => {
                                    carregarCertificados(
                                      extensao.id_contrato.toString(),
                                      extensao.id_adendoaditivo.toString(),
                                    );
                                    onDialogOpen();
                                  }}
                                  cursor="pointer"
                                >
                                  Ver certificados
                                </Text>
                              ) : (
                                <Text>-</Text>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            {extensao.zero_carbono
                              ? `${formatCurrency(
                                  Number(
                                    extensao.zero_carbono.consumo_total_kg,
                                  ),
                                ).replace('R$', '')} kgCO2e`
                              : '-'}
                          </Td>
                          <Td>
                            {extensao.zero_carbono
                              ? `${formatCurrency(
                                  Number(
                                    extensao.zero_carbono.consumo_compensado_kg,
                                  ),
                                ).replace('R$', '')} kgCO2e`
                              : '-'}
                          </Td>
                          <Td>
                            {extensao.zero_carbono
                              ? `${formatCurrency(
                                  Number(extensao.zero_carbono.saldo_em_kg),
                                ).replace('R$', '')} kgCO2e`
                              : '-'}
                          </Td>
                          <Td>
                            {extensao.id_adendoaditivo > 0 && (
                              <ChakraButton
                                variant="link"
                                fontWeight="normal"
                                color="#78797A"
                                onClick={() => handleOpenExtension(extensao)}
                              >
                                Ver detalhes
                              </ChakraButton>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Flex>
              )}
            </TabPanel>
            <TabPanel>
              <Button
                disabled={isLoadingDownload || isLoading}
                leftIcon={<FiDownload />}
                onClick={handleDownloadXlsx}
                color="black"
                background="white"
                _hover={{
                  bgColor: 'white',
                }}
                style={{ marginTop: '10px' }}
              >
                Exportar
                {isLoadingDownload && (
                  <Spinner style={{ marginLeft: '10px' }} />
                )}
              </Button>
              {!!data?.formattedEquipments.length && (
                <Flex flexFlow="column" maxW="1080px" mt="30px">
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th w={130}>Código</Th>
                        <Th w={100}>Tipo</Th>
                        <Th w={225}>Modelo</Th>
                        <Th w={505}>Descrição</Th>
                        <Th>Quantidade</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data?.formattedEquipments.map((equipamento, ieq) => (
                        // ver um jeito de me livrar disso aqui>
                        <>
                          <Tr key={equipamento.id_equipamento} fontSize="14px">
                            <Td>
                              <ChakraButton
                                variant="link"
                                fontWeight="normal"
                                color="#78797A"
                                onClick={() => {
                                  setExpandedEquipment(
                                    expandedEquipment !== ieq ? ieq : -1,
                                  );
                                }}
                              >
                                {expandedEquipment !== ieq ? (
                                  <ChevronRightIcon w={6} h={6} />
                                ) : (
                                  <ChevronDownIcon w={6} h={6} />
                                )}
                                {equipamento.cd_equipamento || '-'}
                              </ChakraButton>
                            </Td>
                            <Td>{equipamento.nm_tipoequipamento || '-'}</Td>
                            <Td>{equipamento.nm_modeloequipamento || '-'}</Td>
                            <Td>{equipamento.ds_equipamento || '-'}</Td>
                            <Td>{equipamento.quantity || 0}</Td>
                            <Td>
                              {equipamento.segurado && (
                                <Tooltip
                                  hasArrow
                                  label={
                                    equipamento.apolice ===
                                    StatusApoliceEquipamentoEnum.ENCERRADAS
                                      ? ''
                                      : 'Equipamento com seguro'
                                  }
                                  bg="black"
                                  shouldWrapChildren
                                >
                                  <svg
                                    width="18"
                                    height="16"
                                    viewBox="0 0 18 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <g clipPath="url(#clip0_10804_21585)">
                                      <path
                                        d="M0 2C0 0.896875 0.896875 0 2 0H7V4C7 4.55312 7.44688 5 8 5H12V6.46875L9.1 7.62812C8.43438 7.89375 8 8.5375 8 9.25312C8 11.0219 8.59063 13.8781 10.9438 15.7625C10.6625 15.9125 10.3406 16 10 16H2C0.896875 16 0 15.1031 0 14V2ZM12 4H8V0L12 4ZM13.2219 7.05312C13.4 6.98125 13.6 6.98125 13.7781 7.05312L17.5281 8.55312C17.8125 8.66875 18 8.94375 18 9.25C18 11.2281 17.1906 14.525 13.7875 15.9437C13.6031 16.0219 13.3938 16.0219 13.2094 15.9437C9.80938 14.525 9 11.2281 9 9.25C9 8.94375 9.1875 8.66875 9.47188 8.55312L13.2219 7.05312ZM16.4813 9.75L13.5 8.55625V14.4281C15.6313 13.3969 16.3594 11.3344 16.4813 9.75Z"
                                        fill="#2C2A29"
                                        opacity={
                                          equipamento.apolice ===
                                          StatusApoliceEquipamentoEnum.ENCERRADAS
                                            ? 0.4
                                            : 1
                                        }
                                      />
                                    </g>
                                    <defs>
                                      <clipPath id="clip0_10804_21585">
                                        <rect
                                          width="18"
                                          height="16"
                                          fill="white"
                                        />
                                      </clipPath>
                                    </defs>
                                  </svg>
                                </Tooltip>
                              )}
                            </Td>
                          </Tr>
                          {/* numeros de serie */}
                          <Tr
                            key={`${equipamento.id_equipamento}-extra`}
                            fontSize="14px"
                            style={{
                              display:
                                expandedEquipment !== ieq
                                  ? 'none'
                                  : 'table-row',
                            }}
                          >
                            <Td
                              colSpan={5}
                              style={{ paddingTop: 0, marginTop: 0 }}
                            >
                              <Stack>
                                {equipamento.nu_series_vigentes.length > 0 && (
                                  <Stack>
                                    <HStack
                                      justifyContent="space-between"
                                      p={1}
                                      borderBottom="1px solid #e5e5e6"
                                      backgroundColor="#fafafa"
                                      borderRadius="4px"
                                    >
                                      <Text fontWeight="bold">Vigente</Text>
                                      <Text fontWeight="bold">
                                        Total de equipamentos vigentes:{' '}
                                        {equipamento.nu_series_vigentes?.length}
                                      </Text>
                                    </HStack>
                                    <Grid
                                      templateColumns="repeat(10, 1fr)"
                                      gap={1}
                                    >
                                      {equipamento.nu_series_vigentes?.map(
                                        nu_serie => (
                                          <GridItem
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            w="100%"
                                            h="5"
                                            key={
                                              nu_serie.id_contratoequipamento
                                            }
                                          >
                                            {nu_serie.nu_serie}
                                          </GridItem>
                                        ),
                                      )}
                                    </Grid>
                                  </Stack>
                                )}
                                {equipamento.nu_series_devolvidos.length >
                                  0 && (
                                  <Stack>
                                    <HStack
                                      justifyContent="space-between"
                                      p={1}
                                      borderBottom="1px solid #e5e5e6"
                                      backgroundColor="#fafafa"
                                      borderRadius="4px"
                                    >
                                      <Text w="" fontWeight="bold">
                                        Devolvido
                                      </Text>
                                      <Text fontWeight="bold">
                                        Total de equipamentos devolvidos:{' '}
                                        {
                                          equipamento.nu_series_devolvidos
                                            ?.length
                                        }
                                      </Text>
                                    </HStack>
                                    <Grid
                                      templateColumns="repeat(10, 1fr)"
                                      gap={1}
                                    >
                                      {equipamento.nu_series_devolvidos?.map(
                                        nu_serie => (
                                          <GridItem
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            w="100%"
                                            h="5"
                                            key={
                                              nu_serie.id_contratoequipamento
                                            }
                                          >
                                            {nu_serie.nu_serie}
                                          </GridItem>
                                        ),
                                      )}
                                    </Grid>
                                  </Stack>
                                )}
                              </Stack>
                            </Td>
                          </Tr>
                        </>
                      ))}
                    </Tbody>
                  </Table>
                </Flex>
              )}
            </TabPanel>
            <TabPanel>
              {!!data?.faturas.length && (
                <Flex flexFlow="column" maxW="955px" mt="30px">
                  <Flex>
                    {(isDownloadingInvoice || isDownloadingBill) && (
                      <Spinner mb={2} ml={3} />
                    )}
                  </Flex>

                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th>N° Fatura</Th>
                        <Th>Mês Referência</Th>
                        <Th textAlign="right">Valor</Th>
                        <Th>Data Vencimento</Th>
                        <Th>Status</Th>
                        <Th>Fatura</Th>
                        <Th>2° via do Boleto</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.faturas.map(invoice => (
                        <Tr key={invoice.id_fatura} fontSize="14px">
                          <Td>
                            <ChakraButton
                              variant="link"
                              fontWeight="normal"
                              color="#78797A"
                              onClick={() =>
                                history.push(
                                  `/portal/invoices/${invoice.id_fatura}`,
                                )
                              }
                              fontSize="14px"
                            >
                              {invoice.nu_fatura}
                            </ChakraButton>
                          </Td>
                          <Td fontSize="14px" textTransform="capitalize">
                            {invoice.formattedIssueDate}
                          </Td>
                          <Td textAlign="right">{invoice.formattedValue}</Td>
                          <Td fontSize="14px">{invoice.formattedDueDate}</Td>
                          <Td>
                            <StatusBadge variant={invoice.colorScheme}>
                              {invoice.formattedStatus}
                            </StatusBadge>
                          </Td>
                          <Td textAlign="center">
                            <IconButton
                              colorScheme="gray"
                              aria-label="Send email"
                              icon={<Icon as={FiDownload} />}
                              onClick={() =>
                                handleDownloadInvoice(
                                  invoice.cd_fatura,
                                  invoice.nu_fatura,
                                )
                              }
                            />
                          </Td>
                          <Td textAlign="center">
                            <IconButton
                              disabled={invoice?.status === 1}
                              colorScheme="gray"
                              aria-label="Send email"
                              icon={<Icon as={FiDownload} />}
                              onClick={() =>
                                handleDownloadBill(
                                  invoice.cd_fatura,
                                  invoice.nu_fatura,
                                )
                              }
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Flex>
              )}
            </TabPanel>
            <TabPanel>
              {!!documentData?.length && (
                <Flex flexFlow="column" maxW="800px" mt="35px">
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th>ID</Th>
                        <Th>Nome do documento</Th>
                        <Th>Data</Th>
                        <Th>Tipo</Th>
                        <Th>Documento</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {documentData.map(document => (
                        <Tr key={document.id_documento} fontSize="14px">
                          <Td>{document.id_documento}</Td>
                          <Td fontSize="14px">{document.nome}</Td>
                          <Td>{document.data_cadastro}</Td>
                          <Td fontSize="14px">{document.nome_tipo}</Td>
                          <Td>
                            <IconButton
                              colorScheme="gray"
                              aria-label="Send email"
                              icon={<Icon as={FiDownload} />}
                              onClick={() => {
                                handleDocumentDownload(document.id_documento);
                              }}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Flex>
              )}
            </TabPanel>
            {data?.contratos_relacionados &&
              data?.contratos_relacionados.length > 0 && (
                <TabPanel>
                  <Flex flexFlow="column" maxW="1080px" mt="30px">
                    <Table type="secondary">
                      <Thead>
                        <Tr>
                          <Th w={250}>N° termo/pedido de locação</Th>
                          <Th w={220}>N° do Contrato</Th>
                          <Th w={180}>Prazo de vigência</Th>
                          <Th w={180} textAlign="end">
                            Valor
                          </Th>
                          <Th style={{ textAlign: 'center' }}>Contrato</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.contratos_relacionados.map(contrato => (
                          <Tr key={contrato.id_contrato}>
                            <Td>{contrato.nu_pedido}</Td>
                            <Td>{contrato.nu_contrato || '-'}</Td>
                            <Td>{contrato.formattedVencimentoFinal}</Td>
                            <Td textAlign="end">
                              {contrato.formattedTotalValue}
                            </Td>
                            <Td style={{ textAlign: 'center' }}>
                              <Link
                                to={`/portal/contracts/${contrato.id_contrato}`}
                                style={{ display: 'inline-block' }}
                              >
                                <BiLinkExternal size={20} />
                              </Link>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Flex>
                </TabPanel>
              )}
            {businessInformationAllow && (
              <TabPanel>
                <HStack
                  fontWeight="bold"
                  w="fit-content"
                  onClick={() => mostrarInformacoesComerciais()}
                  cursor="pointer"
                >
                  <Icon as={canShowInfo ? FaEye : FaEyeSlash} marginRight={3} />
                  <Text fontSize="14px">
                    Clique para exibir as informações comerciais
                  </Text>
                  {loadingBusinessInfo && <Spinner size="sm" />}
                </HStack>
                {canShowInfo && (
                  <Flex flexFlow="column" maxW="1080px" mt="10px">
                    <Table type="secondary">
                      <Thead>
                        <Tr>
                          <Th w={50}> </Th>
                          <Th w={150}>Comissionado</Th>
                          <Th w={120} textAlign="right" whiteSpace="nowrap">
                            Valor do Contrato
                          </Th>
                          <Th w={100}>Tipo</Th>
                          <Th w={120} textAlign="right" whiteSpace="nowrap">
                            % da comissão
                          </Th>
                          <Th w={120} textAlign="right" whiteSpace="nowrap">
                            Total de comissão
                          </Th>
                          <Th w={150} whiteSpace="nowrap">
                            Parcelamento da comissão
                          </Th>
                          <Th w={50}> </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {businessInfo &&
                          businessInfo?.length > 0 &&
                          businessInfo.map((info, ieq) => (
                            <>
                              <Tr
                                key={info.comissionado.codigo_sap}
                                fontSize="14px"
                              >
                                <Td>
                                  <ChakraButton
                                    variant="link"
                                    fontWeight="normal"
                                    color="#78797A"
                                    onClick={() => {
                                      setExpandedInfo(
                                        expandedInfo !== ieq ? ieq : -1,
                                      );
                                    }}
                                  >
                                    {expandedInfo !== ieq ? (
                                      <ChevronRightIcon w={6} h={6} />
                                    ) : (
                                      <ChevronDownIcon w={6} h={6} />
                                    )}
                                  </ChakraButton>
                                </Td>
                                <Td whiteSpace="nowrap">
                                  <Text fontWeight="bolder">
                                    {info.comissionado.nome}
                                  </Text>
                                  <Text fontSize="small">
                                    {info.comissionado.funcao}
                                  </Text>
                                </Td>
                                <Td textAlign="right">
                                  {info.comissionado.valor}
                                </Td>
                                <Td>{info.comissionado.tipo}</Td>
                                <Td textAlign="right">
                                  {info.faturas[0]?.percentual_de_comissao}%
                                </Td>
                                <Td textAlign="right">
                                  {info.comissionado.total_de_comissao}
                                </Td>
                                <Td>{info.comissionado.quantidade_parcelas}</Td>
                                <Td> </Td>
                              </Tr>
                              <Tr
                                key={
                                  info.comissionado.codigo_sap + ieq.toString()
                                }
                                fontSize="14px"
                                style={{
                                  display:
                                    expandedInfo !== ieq ? 'none' : 'table-row',
                                }}
                                backgroundColor="#FAFAFA"
                              >
                                <Th
                                  w={50}
                                  borderTopColor="#8a909c !important"
                                />
                                <Th colSpan={2}>N° Fatura</Th>
                                <Th colSpan={2} whiteSpace="nowrap">
                                  Pagamento da fatura
                                </Th>
                                <Th textAlign="right" whiteSpace="nowrap">
                                  Valor da comissão
                                </Th>
                                <Th whiteSpace="nowrap">Data do Faturamento</Th>
                                <Th w={50} />
                              </Tr>
                              {info.faturas &&
                                info.faturas.map((fatura, index) => (
                                  <Tr
                                    key={`${
                                      fatura.cd_fatura
                                    }-${index.toString()}`}
                                    fontSize="14px"
                                    style={{
                                      display:
                                        expandedInfo !== ieq
                                          ? 'none'
                                          : 'table-row',
                                    }}
                                    backgroundColor="#FAFAFA"
                                  >
                                    <Td w={50} />
                                    <Td colSpan={2}>
                                      <Link
                                        to={`/portal/invoices/${fatura.id_fatura}`}
                                      >
                                        <Text
                                          _hover={{
                                            color: 'gray.900',
                                            textDecoration: 'underline',
                                          }}
                                        >
                                          {fatura.nu_fatura}
                                        </Text>
                                      </Link>
                                    </Td>
                                    <Td colSpan={2} whiteSpace="nowrap">
                                      <StatusBadge
                                        variant={fatura.status_badge}
                                      >
                                        {fatura.status}
                                      </StatusBadge>
                                    </Td>
                                    <Td textAlign="right" whiteSpace="nowrap">
                                      {fatura.valor_comissao}
                                    </Td>
                                    <Td whiteSpace="nowrap">
                                      {fatura.dt_faturamento}
                                    </Td>
                                    <Td w={50} />
                                  </Tr>
                                ))}
                            </>
                          ))}
                        {businessInfo?.length === 0 && (
                          <Tr>
                            <Td colSpan={8} textAlign="center">
                              Nenhuma informação comercial encontrada
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Flex>
                )}
              </TabPanel>
            )}
            {isInterno && !roleCommercialManager && (
              <TabPanel>
                <Stack>
                  {roleAdmin && !isDateInPast(data!.vencimento_final) && (
                    <InsuranceDialog
                      equipaments={data?.formattedEquipments || []}
                      id_contrato={id}
                      isEdit={false}
                      isView={false}
                    />
                  )}
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th w={50} whiteSpace="nowrap">
                          N° da apólice
                        </Th>
                        <Th w={50} whiteSpace="nowrap">
                          Data de início
                        </Th>
                        <Th w={50} whiteSpace="nowrap">
                          Data de encerramento
                        </Th>
                        <Th w={50} whiteSpace="nowrap">
                          Data limite renovação
                        </Th>
                        <Th w={100} whiteSpace="nowrap">
                          Status
                        </Th>
                        <Th w={150} textAlign="center">
                          Ações
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {insuranceData?.length ? (
                        insuranceData.map(item => (
                          <Tr>
                            <Td>{item.nu_apolice}</Td>
                            <Td>{item.formattedStartDate}</Td>
                            <Td>{item.formattedEndDate}</Td>
                            <Td>{item.formattedRenewalLimit}</Td>
                            <Td>
                              <StatusBadge
                                variant={item.statusColor}
                                width="fit-content"
                              >
                                {item.formattedStatus}
                              </StatusBadge>
                            </Td>
                            <Td>
                              <HStack gridGap="1">
                                {item.status !== 2 &&
                                  item.status !== 0 &&
                                  roleAdmin &&
                                  !isDateInPast(data!.vencimento_final) && (
                                    <InsuranceDialog
                                      equipaments={
                                        data?.formattedEquipments || []
                                      }
                                      id_contrato={id}
                                      seguro={item}
                                      isEdit
                                      isView={false}
                                    />
                                  )}
                                {(item.status === 2 ||
                                  item.status === 0 ||
                                  !roleAdmin) &&
                                  !commercialManager && (
                                    <InsuranceDialog
                                      equipaments={
                                        data?.formattedEquipments || []
                                      }
                                      id_contrato={id}
                                      seguro={item}
                                      isEdit={false}
                                      isView
                                    />
                                  )}
                                {!commercialManager && (
                                  <IconButton
                                    aria-label="download"
                                    onClick={() =>
                                      handleDownloadInsurance(item.id)
                                    }
                                    icon={<Icon as={FiDownload} />}
                                  />
                                )}
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={6} textAlign="center">
                            Nenhuma informação de apólice encontrada
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Stack>
              </TabPanel>
            )}
            {features.abaFinanceiroDetalheContratoEnabled &&
              data?.tp_situacao === '1' &&
              isInterno && (
                <TabPanel w="70vw">
                  <ContractCharts
                    contrato={data!}
                    width="100%"
                    data_key={`contract_detail_${data!.id_contrato}`}
                  />
                </TabPanel>
              )}
            {features.abaAnotacoesDetalheContratoEnabled && (
              <TabPanel w="70vw">
                <HStack
                  alignItems="top"
                  as="form"
                  onBlur={handleSubmit(salvarAnotacoes)}
                >
                  <Textarea
                    name="anotacao1"
                    label="Anotação 1:"
                    resize="none"
                    fontSize="15px"
                    placeholder={
                      !roleAdmin ? 'Sem anotação' : 'Adicionar anotação'
                    }
                    isDisabled={!roleAdmin}
                    register={register}
                  />
                  <Textarea
                    name="anotacao2"
                    label="Anotação 2:"
                    resize="none"
                    fontSize="15px"
                    placeholder={
                      !roleAdmin ? 'Sem anotação' : 'Adicionar anotação'
                    }
                    isDisabled={!roleAdmin}
                    register={register}
                  />
                </HStack>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
        {!userIsOperacional && (
          <Stack minWidth="200px">
            <Shortcut
              image={shortcut}
              text="Suporte técnico"
              // alignSelf="flex-start"
              link={ROUTER_PATHS.PORTAL.TECHNICAL_SUPPORT}
            />
          </Stack>
        )}
      </Flex>

      <Drawer
        isOpen={isEquipmentOpen}
        placement="right"
        onClose={onEquipmentClose}
        size="md"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="1.5rem">Detalhes do Equipamento</Heading>
          </DrawerHeader>

          <DrawerBody>
            {isEquipmentLoading ? (
              <Flex justify="center">
                <Spinner />
              </Flex>
            ) : isEquipmentError ? (
              <Flex justify="center">
                <Text>Falha ao obter dados do equipamento.</Text>
              </Flex>
            ) : (
              <Stack gridGap={6}>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Tipo Equipamento</Heading>
                  <Text>
                    {equipmentData?.tipo_equipamento?.nm_tipoequipamento || '-'}
                  </Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Modelo</Heading>
                  <Text>
                    {equipmentData?.modelo_equipamento?.nm_modeloequipamento ||
                      '-'}
                  </Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Especificação técnica</Heading>
                  <Text>{equipmentData?.ds_equipamento || '-'}</Text>
                </Flex>
                <Table style={{ border: 'none' }}>
                  <Thead>
                    <Th>Chamado</Th>
                    <Th>Data abertura</Th>
                    <Th>Status</Th>
                  </Thead>
                  <Tbody>
                    {equipmentData?.formattedOccurrences &&
                      equipmentData.formattedOccurrences.map(occurrence => (
                        <Tr key={occurrence.id_chamado}>
                          <Td>
                            <ChakraButton
                              variant="link"
                              fontWeight="normal"
                              color="#78797A"
                              onClick={() =>
                                history.push(
                                  replaceParameters(
                                    ROUTER_PATHS.PORTAL.OCCURRENCE_DETAIL,
                                    String(occurrence.id_chamado),
                                  ),
                                )
                              }
                            >
                              {occurrence.nu_serie}
                            </ChakraButton>
                          </Td>
                          <Td>{occurrence.formattedOpeningDate}</Td>
                          <Td>
                            <StatusBadge variant={occurrence.colorScheme || ''}>
                              {occurrence.formattedStatus}
                            </StatusBadge>
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </Stack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Drawer
        isOpen={isExtensionOpen}
        placement="right"
        onClose={onExtensionClose}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="1.5rem">
              Detalhes da Extensão N° {selectedExtension.cd_adendoaditivo}
            </Heading>
          </DrawerHeader>

          <DrawerBody>
            <HStack justify="space-between">
              <Stack gridGap={6} w="50%">
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Data Início</Heading>
                  <Text>{selectedExtension.formattedInitialDate}</Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Valor Total</Heading>
                  <Text>{selectedExtension.formattedTotalValue}</Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Índice de correção</Heading>
                  <Text>{selectedExtension.nm_indicecorrecao}</Text>
                </Flex>
              </Stack>
              <Stack gridGap={6} w="50%">
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Prazo em Meses</Heading>
                  <Text>{selectedExtension.differenceInMonths}</Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Valor Mensal</Heading>
                  <Text>{selectedExtension.formattedMonthlyValue}</Text>
                </Flex>
                <Flex flexDir="column" gridGap={2}>
                  <Heading fontSize="1rem">Mês de ajuste do contrato</Heading>
                  <Text textTransform="capitalize">
                    {selectedExtension.formattedMonth}
                  </Text>
                </Flex>
              </Stack>
            </HStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={undefined}
        onClose={onDialogClose}
        isOpen={isDialogOpen}
        isCentered
        size="6xl"
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>Certificados</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Stack>
              <Table type="secondary">
                <Thead>
                  <Tr>
                    <Th>CERTIFICADO</Th>
                    <Th>DATA DE CERTIFICAÇÃO</Th>
                    <Th>COMPENSADO</Th>
                    <Th>SITUAÇÃO</Th>
                    <Th textAlign="center">
                      AÇÃO {isDownloadingCertificados && <Spinner />}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {certificados?.length ? (
                    certificados?.map(a => (
                      <Tr key={a.id}>
                        <Td>{a.codigo_certificado ?? '-'}</Td>
                        <Td>{a.formattedDataDeCertificacao}</Td>
                        <Td>{a.formattedConsumoCompensadoCo2Kg} kgCO2e</Td>
                        <Td>
                          <StatusBadge
                            variant={a.statusColor}
                            width="fit-content"
                          >
                            {a.formattedStatus}
                          </StatusBadge>
                        </Td>
                        <Td>
                          <HStack gridGap={2} justifyContent="center">
                            <Tooltip
                              hasArrow
                              label="Abrir certificado"
                              bg="black"
                              shouldWrapChildren
                            >
                              <IconButton
                                marginRight={2}
                                aria-label="Abrir Certificado"
                                icon={<Icon as={FiExternalLink} />}
                                isDisabled={!a.codigo_certificado}
                                onClick={() =>
                                  openLinkInNewTab(`${a.link_certificado}`)
                                }
                              />
                            </Tooltip>
                            <Tooltip
                              hasArrow
                              label="Certificado Certificadora"
                              bg="black"
                              shouldWrapChildren
                            >
                              <IconButton
                                aria-label="Download certificados da certificadora"
                                icon={<Icon as={FiDownload} />}
                                isDisabled={
                                  isDownloadingCertificados ||
                                  !a.codigo_certificado
                                }
                                onClick={() =>
                                  downloadCertificados(
                                    a.id_grupo_economico_projeto_co2.toString(),
                                    'certificadora',
                                  )
                                }
                              />
                            </Tooltip>

                            <Tooltip
                              hasArrow
                              label="Certificado Validadora"
                              bg="black"
                              shouldWrapChildren
                            >
                              <IconButton
                                aria-label="Download certificados da validadora"
                                icon={<Icon as={FiDownload} />}
                                isDisabled={
                                  isDownloadingCertificados ||
                                  !a.codigo_certificado
                                }
                                onClick={() =>
                                  downloadCertificados(
                                    a.id_grupo_economico_projeto_co2.toString(),
                                    'validadora',
                                  )
                                }
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      {!isLoagingCertificados ? (
                        <Td textAlign="center" colSpan={6}>
                          Não há dados a serem apresentados
                        </Td>
                      ) : (
                        <Td textAlign="center" colSpan={6}>
                          <Spinner />
                        </Td>
                      )}
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onDialogClose} variant="secondary">
              Fechar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};
